/**
 * Rune Stage Route
 *
 * Triggers loading of the rune stage when this route becomes active.
 * Supports rune variants via ?glb query parameter.
 * The actual canvas is in the parent layout (ux.tsx).
 *
 * When a non-Babylon viewer is selected, the BabylonJS stage load is skipped
 * and a lazy-loaded Three.js or model-viewer component renders the GLB instead.
 *
 * Note: Rune Babylon stage uses `variants` (not `glbKey`) for config overrides,
 * since it constructs meshes from extracted vertex data rather than loading GLB.
 */

import { createFileRoute } from "@tanstack/react-router";
import { lazy, Suspense, useMemo } from "react";
import { z } from "zod";
import { useViewerRefs } from "@/babylon/context/ViewerRefsContext";
import {
  DEFAULT_RUNE_GLB_KEY,
  RUNE_GLB_CONFIGS,
} from "@/babylon/stages/rune.stage";
import { resolveRuneGlbUrl } from "@/babylon/utils/glbPaths";
import { useViewer } from "@/context/ViewerContext";
import { useLoadStageOnMount } from "../ux";

const ThreeGlbViewer = lazy(
  () => import("@/babylon/components/viewers/ThreeGlbViewer"),
);
const ModelViewerGlb = lazy(
  () => import("@/babylon/components/viewers/ModelViewerGlb"),
);

/** Valid GLB keys derived from config */
const runeGlbKeys = Object.keys(RUNE_GLB_CONFIGS) as [string, ...string[]];

/** Zod schema for rune route search params */
const runeSearchSchema = z.object({
  glb: z.enum(runeGlbKeys).optional().default(DEFAULT_RUNE_GLB_KEY),
});

export const Route = createFileRoute("/_immersive/demo/3d/ux/rune")({
  component: RuneStageRoute,
  validateSearch: (search) => runeSearchSchema.parse(search),
});

function RuneStageRoute() {
  const { glb } = Route.useSearch();
  const { viewerType } = useViewer();
  const { setThreeRefs, setModelViewerElement } = useViewerRefs();
  const glbUrl = resolveRuneGlbUrl(glb);

  // Rune stage reads stageConfig.variants, NOT glbKey
  const configOverrides = useMemo(() => ({ count: 1, variants: [glb] }), [glb]);

  // Always call hook unconditionally (React rules of hooks), gate with enabled
  useLoadStageOnMount("rune", configOverrides, viewerType === "babylon");

  if (viewerType === "babylon") return null;

  return (
    <div className="w-full h-full pointer-events-auto">
      <Suspense
        fallback={
          <div className="w-full h-full flex items-center justify-center text-gray-500">
            Loading viewer...
          </div>
        }
      >
        {viewerType === "three.js" && (
          <ThreeGlbViewer glbUrl={glbUrl} onRefsReady={setThreeRefs} />
        )}
        {viewerType === "model-viewer" && (
          <ModelViewerGlb
            glbUrl={glbUrl}
            onElementReady={setModelViewerElement}
          />
        )}
      </Suspense>
    </div>
  );
}
